import React, { useState, useEffect } from 'react'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { Game, getAllGames } from '@/lib/games'
import { clickGame, clickPage, ttqTrack } from '@/utils/ga'
import Meta from '@/components/Meta'
import Link from 'next/link'
import Image from 'next/image'
import styles from '@/styles/index.module.scss'
import {
  getInfoByPath,
  convertTagsToArray,
  handleClean,
  getUrlParams,
} from '@/utils/helper'
import { addGameToHistory } from '@/utils/gameStorage'
import icHome from '@/images/ic_home.png'
import logo from '@/images/logo.png'
import dynamic from 'next/dynamic'

const AdsenseAd = dynamic(() => import('@/components/AdsenseAd'), {
  ssr: false,
})

export default function GameInfo(props: { res: any; gamesList: Game[] }) {
  const router = useRouter()
  const { query } = router
  const { slug } = router.query
  const campaignParams = Object.fromEntries(
    Object.entries(router.query).filter(([key]) => key !== 'slug')
  )
  const linkParams = getUrlParams(query?.utm_campaign as string)

  const { res, gamesList } = props
  const [play, setPlay] = useState(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)

  // 路由变化时重置状态的 useEffect
  useEffect(() => {
    if (res) {
      // 只在 res 存在时执行 gtag
      setPlay(false)
      setIframeLoaded(false)
      window.gtag('event', 'view_game_detail', {
        game_id: res.id,
        game_name: res.title,
      })
    }
  }, [slug, res]) // 添加 res 作为依赖项

  // 如果没有找到结果，提前返回空内容
  if (!res) {
    return null
  }

  const { category, tags } = convertTagsToArray(res)

  const handlePlay = (id: string, title: string) => {
    addGameToHistory(res)
    window.gtag('event', 'click_play_now', {
      game_id: id,
      game_name: title,
    })
    ttqTrack('AddToCart')
    setPlay(true)
  }

  const handleClickCategory = (item: string) => {
    clickPage('category', 'game')
    const catrgory = item.toLowerCase().replace(/\s+/g, '-')
    router.push(`/c/${catrgory}`)
  }

  const handleClickTag = (item: string) => {
    clickPage('tag', 'game')
    const tag = item.toLowerCase().replace(/\s+/g, '-')
    router.push(`/t/${tag}`)
  }

  return (
    <>
      <Meta
        title={`${res.title} | Play on h5freegames.com`}
        desc={res.description.slice(0, 150)}
      />

      <div className={styles.gameInfo}>
        {play ? (
          <>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 600,
                maxWidth: 500,
                margin: 'auto auto 30px',
              }}
            >
              {/* 封面图：当 iframe 未加载完成时显示 */}
              {!iframeLoaded && (
                <>
                  <Image
                    src={res.thumb}
                    alt={res.title}
                    width={512}
                    height={384}
                  />{' '}
                  <div className={styles.loading}></div>
                </>
              )}

              {/* iframe：加载完成后显示 */}
              <iframe
                src={res.url}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 5,
                  display: iframeLoaded ? 'block' : 'none', // 未加载完成时隐藏
                }}
                onLoad={() => setIframeLoaded(true)} // iframe 加载完成后触发
              />
            </div>
          </>
        ) : (
          <>
            <div
              className={styles.playBox}
              onClick={() => handlePlay(res.id, res.title)}
            >
              <Image
                src={res.thumb}
                alt={res.title}
                width={160}
                height={120}
                priority={true}
              />
              <Image
                src={res.thumb}
                alt={res.title}
                width={500}
                height={300}
                priority={true}
              />
              <div className={styles.play}>
                {' '}
                <img src={logo.src} alt="logo" />
                {'Play Now'}
              </div>
            </div>
            <AdsenseAd slot="7451201292" />
            <div className={styles.detail}>
              <div className={styles.titleBox}>
                <h1 className={styles.title}>{res.title}</h1>
                <p>{res.description}</p>
              </div>
              <i className={styles.tips}>
                {
                  'This game is powered by GameMonetize.com, which retains all rights to the game.'
                }
              </i>
              <div className={styles.category}>
                <h2>Category</h2>
                <div>
                  {category.map((item: string) => (
                    <span key={item} onClick={() => handleClickCategory(item)}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.tag}>
                <h2>Tag</h2>
                <div>
                  {tags.map((tag: string) => (
                    <span key={tag} onClick={() => handleClickTag(tag)}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        <div className={styles.hot}>
          <h2>👍 Recommended Games</h2>
          <div className={styles.hotList}>
            {gamesList.map((item: any, index: number) => (
              <Link
                key={item.id + index}
                href={{
                  pathname: `/game/${item.slug}`,
                  query: campaignParams,
                }}
                className={styles.item}
                onClick={() => {
                  clickGame(item.typeId, item.id, item.title, 'game')
                  handleClean(linkParams)
                }}
              >
                <Image
                  src={item.thumb}
                  alt={item.title}
                  loading="lazy"
                  width={118}
                  height={88}
                />
                <p>{item.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const gameListArr = await getAllGames()
  const paths = gameListArr.map((game) => ({
    params: { slug: game.slug },
  }))
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string
  const gameListArr = await getAllGames()
  const isValidSlug = gameListArr.some((game) => game.slug === slug)
  if (!isValidSlug) return { notFound: true }
  const res = getInfoByPath(gameListArr, slug as string)
  // 这里只取 typeId 为 003 的游戏数据
  const allGames = [...gameListArr]
    .filter((item) => item.typeId === '003')
    .sort(() => Math.random() - 0.5)

  const gamesList = allGames.map((game) => ({
    id: game.id,
    title: game.title,
    slug: game.slug,
    thumb: game.thumb,
    category: game.category,
    tags: game.tags,
    typeId: game.typeId,
  }))

  return { props: { res, gamesList }, revalidate: 60 }
}
